/**
 * Event types slice of the English catalog — the Business Hub → Calendar
 * "Event types" panel, the location of an appointment, and the public booking
 * page's location line and phone requirement.
 *
 * Spread into `enScheduling` (not into en.ts, which sits at the 500-line
 * ceiling), so `t()` and MessageKey see it unchanged.
 */
export const enEventTypes = {
  // ── Business Hub → Calendar → Event types ─────────────────────────────
  'eventTypes.title': 'Event types',
  'eventTypes.help':
    'Each event type is something a customer can book, with its own length, price, place and link.',
  'eventTypes.new': 'New event type',
  'eventTypes.empty': 'No event types yet. Create one to get a booking link.',
  'eventTypes.loading': 'Loading event types…',
  'eventTypes.loadFailed': 'Could not load your event types. Refresh the page to try again.',
  'eventTypes.minutes': '{n} min',
  'eventTypes.price': 'Price {price}',
  'eventTypes.noPrice': 'No price set',
  'eventTypes.copyLink': 'Copy link',
  'eventTypes.copied': 'Copied',
  'eventTypes.edit': 'Edit',
  'eventTypes.archive': 'Archive',
  'eventTypes.archived': 'Event type archived. Its link no longer takes bookings.',
  'eventTypes.archiveFailed': 'Could not archive the event type. Please try again.',
  'eventTypes.created': 'Event type created. Its booking link is ready to share.',
  'eventTypes.saved': 'Event type saved.',
  'eventTypes.saveFailed': 'Could not save the event type. Check the fields and try again.',

  'eventTypes.location.buzzcall': 'Video call (BuzzCall)',
  'eventTypes.location.in_person': 'In person',
  'eventTypes.location.phone': 'Phone call',
  'eventTypes.location.custom_link': 'Your own meeting link',

  'eventTypes.form.createTitle': 'New event type',
  'eventTypes.form.editTitle': 'Edit event type',
  'eventTypes.form.title': 'Name',
  'eventTypes.form.description': 'Description',
  'eventTypes.form.duration': 'Length (min)',
  'eventTypes.form.price': 'Price',
  'eventTypes.form.deposit': 'Deposit',
  'eventTypes.form.bufferBefore': 'Buffer before (min)',
  'eventTypes.form.bufferAfter': 'Buffer after (min)',
  'eventTypes.form.minNotice': 'Minimum notice (min)',
  'eventTypes.form.window': 'Bookable up to (days ahead)',
  'eventTypes.form.increment': 'Start times every (min)',
  'eventTypes.form.dailyCap': 'Max per day',
  'eventTypes.form.weeklyCap': 'Max per week',
  'eventTypes.form.optionalHint': 'Leave a box empty for no limit or no price.',
  'eventTypes.form.color': 'Calendar colour',
  'eventTypes.form.location': 'Location',
  'eventTypes.form.address': 'Address',
  'eventTypes.form.notes': 'Notes for the customer',
  'eventTypes.form.url': 'Meeting link (https://)',
  'eventTypes.form.buzzcallNote': 'Each booking gets its own BuzzCall video room.',
  'eventTypes.form.phoneNote': 'Customers must give a phone number to book this type.',
  'eventTypes.form.save': 'Save',
  'eventTypes.form.create': 'Create',
  'eventTypes.form.saving': 'Saving…',
  'eventTypes.form.cancel': 'Cancel',
  'eventTypes.form.titleRequired': 'Give the event type a name.',
  'eventTypes.form.invalidNumber': 'Use whole numbers of zero or more, or leave the box empty.',
  'eventTypes.form.durationRange': 'The length must be between 5 and 480 minutes.',
  'eventTypes.form.urlRequired': 'Enter the full meeting link, starting with https://.',
  'eventTypes.form.questionsAfterCreate': 'You can add questions once the event type is created.',

  // ── Booking questions: the two validated types ────────────────────────
  'booking.questions.typePhone': 'Phone',
  'booking.questions.typeEmail': 'Email',

  // ── Public booking page (/book/:slug) — where, and the phone rule ─────
  'booking.public.location.buzzcall': 'Video call. You will get the link after you book.',
  'booking.public.location.in_person': 'In person',
  'booking.public.location.phone': 'Phone call. We will call you on the number you give.',
  'booking.public.location.custom_link': 'Online meeting. You will get the link after you book.',
  'booking.public.openMaps': 'Open in Google Maps',
  'booking.public.phoneRequiredLabel': 'Phone',
  'booking.public.phoneRequiredHint': 'Needed for this appointment, so the business can reach you.',
  'booking.public.phoneRequired':
    'This appointment needs a phone number. Please add one, including the country code.',
} as const;
